"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import { getLocaleFromPathname, withLocale } from "@/lib/i18n";
import { usePathname } from "next/navigation";
import { getDictionary } from "@/lib/messages";

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

const STORAGE_KEY = "dogufy-cookie-consent";
const GA_ID = "G-HXWK5X14Q5";

type ConsentState = "accepted" | "rejected" | null;

function applyConsent(consent: Exclude<ConsentState, null>) {
  if (typeof window === "undefined") return;

  window.dataLayer = window.dataLayer || [];
  window.gtag =
    window.gtag ||
    function gtag(...args: unknown[]) {
      window.dataLayer?.push(args);
    };

  window.gtag("consent", "update", {
    analytics_storage: consent === "accepted" ? "granted" : "denied",
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
    functionality_storage: "granted",
    personalization_storage: "denied",
    security_storage: "granted",
  });

  if (consent === "accepted" && !document.querySelector(`script[data-ga-id="${GA_ID}"]`)) {
    const script = document.createElement("script");
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
    script.dataset.gaId = GA_ID;
    document.head.appendChild(script);

    window.gtag("js", new Date());
    window.gtag("config", GA_ID, {
      anonymize_ip: true,
    });
  }
}

export function CookieConsent() {
  const [consent, setConsent] = useState<ConsentState>(null);
  const pathname = usePathname() || "/";
  const locale = getLocaleFromPathname(pathname);
  const dict = getDictionary(locale);

  useEffect(() => {
    const stored = window.localStorage.getItem(STORAGE_KEY) as ConsentState;
    if (stored === "accepted" || stored === "rejected") {
      setConsent(stored);
      applyConsent(stored);
    }
  }, []);

  if (consent) {
    return null;
  }

  const persistConsent = (nextConsent: Exclude<ConsentState, null>) => {
    window.localStorage.setItem(STORAGE_KEY, nextConsent);
    setConsent(nextConsent);
    applyConsent(nextConsent);
  };

  return (
    <div className="fixed inset-x-4 bottom-4 z-[70] mx-auto max-w-3xl rounded-[28px] border border-positivus-dark bg-white p-5 shadow-[0_8px_0_0_#191A23] md:inset-x-0">
      <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <div className="max-w-2xl">
          <p className="mb-2 text-lg font-semibold text-positivus-dark">{dict.cookie.title}</p>
          <p className="text-sm leading-6 text-gray-600">
            {dict.cookie.body}
          </p>
          <p className="mt-2 text-sm leading-6 text-gray-600">
            <Link href={withLocale(locale, "/privacy")} className="font-medium text-positivus-dark underline underline-offset-4">
              {dict.cookie.privacy}
            </Link>
          </p>
        </div>

        <div className="flex flex-col gap-3 sm:flex-row">
          <button
            type="button"
            onClick={() => persistConsent("rejected")}
            className="h-11 rounded-[14px] border border-positivus-dark px-5 text-sm font-medium text-positivus-dark transition-colors hover:bg-positivus-grey"
          >
            {dict.cookie.reject}
          </button>
          <button
            type="button"
            onClick={() => persistConsent("accepted")}
            className="h-11 rounded-[14px] bg-positivus-dark px-5 text-sm font-medium text-white transition-colors hover:bg-positivus-green hover:text-positivus-dark"
          >
            {dict.cookie.accept}
          </button>
        </div>
      </div>
    </div>
  );
}
